import React, { useState, useEffect, useRef } from 'react';
import useStyles from './styles';
import { get, size } from 'global/lib/lodash';
import { Text } from 'global/components/text';
import { VideoPlayer } from 'global/components/videoplayer';
import { Column, Row } from 'global/components/row';
import { useWindowSize } from 'global/hooks/useWindowSize';
import { Button } from 'global/components/Button';
import { Icon, Tooltip } from '@material-ui/core';
import { cleanCopy } from 'global/utils';
import sendData from 'redux/actions/sendData';
import SnackAlerts from 'global/components/snack-alerts';
import { Quiz } from './quiz';
import { Checklist } from './checklist';
import TabContainer from 'global/components/TabContainer';
import { Assessment } from './assessment';
import withRouter from 'react-router-dom/withRouter';
import { useScreen } from 'global/styles';

const defaultState = { watched: false };

export default withRouter((props) => {
  const classes = useStyles();
  const ref = useRef();
  const [state, setState] = useState(cleanCopy(defaultState));
  const [alerts, setAlerts] = useState();

  const { setDoc, loading, docIndex } = props;
  const { isMobile } = useScreen();

  useWindowSize();

  const { isReview } = get(props, 'location.state', {});

  const document = get(props, 'document.document');
  const file = get(document, 'file');
  const isAssessment = get(props, 'document.documentableType') === 'assessment';
  const isStep = get(props, 'document.documentableType') === 'step_by_step';
  const isChecklistOrQuiz = get(props, 'document.documentableType') === 'checklist';
  const isQuiz = get(props, 'document.meta.type') === 'quiz';
  const isChecklist = get(props, 'document.meta.type') === 'checklist' || (isChecklistOrQuiz && !isQuiz);

  const updateReadStatus = async (completed = false) => {
    try {
      if (isReview) {
        return;
      }

      const reads = get(props, 'document.reads', []);
      const hasRead = size(reads) > 0;
      const incompleteRead = reads.find((r) => !r.completedAt);

      let shouldUpdate = false;

      if (!completed && !hasRead) {
        shouldUpdate = true;
        await sendData({
          url: `/read-statuses`,
          method: 'post',
          data: {
            data: {
              type: 'read-statuses',
              attributes: {
                readable_type: 'edition_document',
                readable_id: get(props, 'document.id'),
              },
            },
          },
        });
      } else if (completed && incompleteRead) {
        shouldUpdate = true;
        await sendData({
          url: `/read-statuses/${incompleteRead.id}`,
          method: 'patch',
          data: {
            data: {
              type: 'read-statuses',
              id: incompleteRead.id,
              attributes: {
                completed_at: new Date().toISOString(),
              },
            },
          },
        });
      }

      if (shouldUpdate) {
        props.update && (await props.update());
      }
    } catch (err) {
      setAlerts({
        message: `Failed to update read status: ${err.message}`,
        severity: 'error',
      });
    }
  };

  useEffect(() => {
    if (document) {
      const reads = get(props, 'document.reads', []);
      const watched = reads.find((r) => !!r.completedAt);
      setState(cleanCopy({ ...defaultState, watched }));
      updateReadStatus();
    }
  }, [document]);

  const onProgress = (p) => {
    const { played } = p;
    if (played >= 1 && !state.watched) {
      setState((s) => ({ ...s, watched: true }));
      updateReadStatus(true);
    }
  };

  if (isQuiz) {
    return (
      <div className={classes.root}>
        <TabContainer useAbsolute={!isMobile} height={window.innerHeight - 146}>
          <Quiz key={document.id} {...props} updateReadStatus={updateReadStatus} />
        </TabContainer>
      </div>
    );
  } else if (isAssessment) {
    return (
      <div className={classes.root}>
        <TabContainer useAbsolute={!isMobile} height={window.innerHeight - 146}>
          <Assessment key={document.id} {...props} updateReadStatus={updateReadStatus} />
        </TabContainer>
      </div>
    );
  } else if (isChecklist || isStep) {
    return (
      <div className={classes.root}>
        <TabContainer useAbsolute={!isMobile} height={window.innerHeight - 146}>
          <Checklist key={document.id} {...props} updateReadStatus={updateReadStatus} />
        </TabContainer>
      </div>
    );
  }

  const calcDims = () => {
    const maxHeight = 450;
    const width = get(ref, 'current.clientWidth', 560) - 16 * 4;
    const height = width * 0.6;

    if (height > maxHeight) {
      return { width: maxHeight / 0.6, height: maxHeight };
    } else {
      return { width, height };
    }
  };

  const url = get(file, 'url');

  const dims = calcDims();

  return (
    <div className={classes.root}>
      <div className="content" ref={ref}>
        {!url && <Text>{loading ? 'Loading...' : 'No File'}</Text>}
        {!!url && (
          <Column center>
            <VideoPlayer {...dims} url={url} trackBaseChange onProgress={onProgress} />
            <Row className="buttonRow">
              <Tooltip title={'Previous'}>
                <span>
                  <Button disabled={docIndex.current === 0} onClick={() => setDoc(-1)} startIcon={<Icon>arrow_left</Icon>}>
                    Prev
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={!state.watched ? 'Please finish watching this video before proceeding.' : 'Next'}>
                <span>
                  <Button disabled={!state.watched || !docIndex.canNext} onClick={() => setDoc(1)} startIcon={<Icon>arrow_right</Icon>}>
                    Next
                  </Button>
                </span>
              </Tooltip>
            </Row>
          </Column>
        )}
      </div>
      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </div>
  );
});
